import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PuckPage } from "src/api/client-api/cms/useCreatePuckPage";
import { EmptyStateCard } from "src/components/EmptyState/EmptyStateCard";
import { usePdfExport } from "src/hooks/usePdfExport";

import { getExportOptionsFromTemplate } from "./publicationPuckPdfUtils";
import { PublicationCard } from "./PublicationCard";
import { PublicationPreviewDialog } from "./PublicationPreviewDialog";
import { useMyPublicationsData } from "./useMyPublicationsData";
import { containerSx, gridSx, headerIconBoxSx } from "./styles";
import { PuckDataProvider } from "src/components/Puck/config/wrappers/PuckDataProvider";
import { useTokenResolver } from "src/components/Puck/config/fields/extensions/typingSuggestions/hooks/useTokenResolver";

type MyPublicationsProps = {
    scoreIdsFilter?: number[];
};

export const MyPublications = (props: MyPublicationsProps) => {
    return (
        <PuckDataProvider>
            <MyPublicationsContent {...props} />
        </PuckDataProvider>
    );
};

const MyPublicationsContent = (props: MyPublicationsProps) => {
    const { t } = useTranslation();
    const { exportPage } = usePdfExport();
    const { processToken } = useTokenResolver();

    const {
        hasTemplates,
        isPagesLoading,
        isPagesError,
        isVisibilityLoading,
        visiblePublications,
        availableCount,
        notAvailableCount,
        buildExportUrl,
        generateFilename,
    } = useMyPublicationsData({ scoreIdsFilter: props.scoreIdsFilter });

    const [preview, setPreview] = useState<PuckPage | null>(null);
    const [downloadingBySlug, setDownloadingBySlug] = useState<Record<string, boolean>>({});

    const isSettled = !isPagesLoading && !isPagesError && !isVisibilityLoading;

    // No template configured at all: the block has nothing to say, stay out of the page.
    if (isSettled && !hasTemplates) {
        return null;
    }

    const handleDownload = async (page: PuckPage, scoreIdForExport: number | null) => {
        const url = page.url;
        if (typeof url !== "string" || url.trim() === "") return;

        setDownloadingBySlug((prev) => ({
            ...prev,
            [page.slug]: true,
        }));
        try {
            const exportUrl = buildExportUrl(url, scoreIdForExport);
            const filename = generateFilename(url);
            const options = getExportOptionsFromTemplate(page);

            if (options.pdfHeader) {
                options.pdfHeader = {
                    ...options.pdfHeader,
                    logoUrl: processToken(options.pdfHeader.logoUrl),
                    title: processToken(options.pdfHeader.title),
                    subtitle: processToken(options.pdfHeader.subtitle),
                };
            }
            if (options.pdfFooter) {
                options.pdfFooter = {
                    ...options.pdfFooter,
                    text: processToken(options.pdfFooter.text),
                };
            }

            await exportPage(exportUrl, filename, options);
        } finally {
            setDownloadingBySlug((prev) => {
                const next = { ...prev };
                delete next[page.slug];
                return next;
            });
        }
    };

    return (
        <Box sx={containerSx}>
            <Stack direction="row" alignItems="center" gap="16px">
                <Box sx={headerIconBoxSx}>
                    <ArticleOutlinedIcon sx={{ fontSize: 28, color: "#CF960F" }} />
                </Box>

                <Stack>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.6, color: "#393A36" }}>
                        {t("publications.title", "My publications")}
                    </Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 400, lineHeight: 1.6, color: "#767673" }}>
                        {t("publications.counts", "{{available}} available • {{notAvailable}} not available", {
                            available: availableCount,
                            notAvailable: notAvailableCount,
                        })}
                    </Typography>
                </Stack>
            </Stack>

            {(isPagesLoading || isVisibilityLoading) && (
                <Stack alignItems="center" justifyContent="center" sx={{ py: 2 }}>
                    <CircularProgress />
                </Stack>
            )}

            {!isPagesLoading && isPagesError && (
                <Typography sx={{ fontSize: 12, fontWeight: 500, lineHeight: 1.6, color: "#767673" }}>
                    {t("publications.error", "Unable to load publications")}
                </Typography>
            )}

            {isSettled && visiblePublications.length === 0 && (
                <EmptyStateCard
                    title={t("publications.empty.title", "No publication available")}
                    description={t(
                        "publications.empty.description",
                        "No publication is available for the selected organization.",
                    )}
                />
            )}

            {isSettled && visiblePublications.length > 0 && (
                <Box sx={gridSx}>
                    {visiblePublications.map((item) => (
                        <PublicationCard
                            key={item.page.slug}
                            page={item.page}
                            isAvailable={item.isAvailable}
                            scoreTitle={item.scoreTitle}
                            isDownloading={downloadingBySlug[item.page.slug] === true}
                            onDownload={(p) => handleDownload(p, item.scoreIdForExport)}
                            onPreview={(p) => setPreview(p)}
                        />
                    ))}
                </Box>
            )}

            <PublicationPreviewDialog
                preview={preview}
                onClose={() => {
                    setPreview(null);
                }}
            />
        </Box>
    );
};

export default MyPublications;
